import React, { useMemo, useState } from 'react';
import { docTypeLabel, resolvedDocType, sourceFormat } from '../diligence/docTypes.js';
import { deriveKb } from './kb.js';

const CONF = { HIGH: 'aw-pill-green', MED: 'aw-pill-amber', LOW: 'aw-pill-orange', GAP: 'aw-pill-red' };
const FORECAST_TYPES = new Set(['production_history', 'seller_forecast']);

function typeLabel(d) {
  if (!d.docType) return '—';
  const label = docTypeLabel(d.docType);
  if (!FORECAST_TYPES.has(d.docType)) return label;
  const fmt = sourceFormat(d);
  return fmt ? `${label} · ${fmt}` : label;
}

export default function KnowledgeBase({ wf, reviews, onFocus }) {
  const { facts, gaps, sections } = useMemo(() => deriveKb(wf, reviews), [wf, reviews]);
  const [q, setQ] = useState('');
  const match = (t) => !q || t.toLowerCase().includes(q.toLowerCase());
  return (
    <div className="aw-kb" data-testid="kb">
      <div className="aw-card">
        <input className="aw-input" placeholder="Search facts and gaps…" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="aw-readiness">
          {sections.map((s) => (
            <div key={s.name} className="aw-ready-row">
              <span>{s.name}</span>
              <div className="aw-bar"><i style={{ width: `${s.pct}%` }} /></div>
              <span className="aw-mono aw-small">{s.pct}% · {s.facts} facts · {s.gaps} gaps</span>
            </div>
          ))}
        </div>
      </div>
      <div className="aw-cards">
        {gaps.filter((g) => match(g.title)).map((g) => (
          <div key={g.id} className="aw-card aw-gap" data-testid="kb-gap">
            <div className="aw-toolbar"><span className="aw-mono aw-small">{g.id}</span><span className="aw-pill aw-pill-red">GAP</span><span className="aw-eyebrow">{g.section}</span></div>
            <div className="aw-fact-title">{g.title}</div>
            <div className="aw-small"><b>Blocks:</b> {g.blocks}</div>
            <div className="aw-small"><b>Closes it:</b> {g.closes}</div>
            <div className="aw-small"><b>Owner:</b> {g.owner}</div>
            {g.wells.length > 0 && (
              <div className="aw-flags">
                {g.wells.slice(0, 8).map((id) => (
                  <button key={id} type="button" className="aw-chip" onClick={() => onFocus(id, 'decline')}>{wf.wells.find((w) => w.assetId === id)?.name || id}</button>
                ))}
                {g.wells.length > 8 && <span className="aw-chip">+{g.wells.length - 8}</span>}
              </div>
            )}
          </div>
        ))}
        {facts.filter((f) => match(f.title)).map((f) => (
          <div key={f.id} className="aw-card" data-testid="kb-fact">
            <div className="aw-toolbar"><span className="aw-mono aw-small">{f.id}</span><span className={`aw-pill ${CONF[f.confidence] || ''}`}>{f.confidence}</span><span className="aw-eyebrow">{f.section}</span></div>
            <div className="aw-fact-title">{f.title}</div>
            <div className="aw-flags">
              {f.evidence.map((e) => (
                <span key={e.label} className="aw-chip" title={e.sha ? `sha256 ${e.sha}` : ''}>{e.label}{e.sha ? ` · ${e.sha.slice(0, 8)}` : ''}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DataRoom({ wf, focusDocId = '', docType = '', onClearFilter }) {
  const docs = useMemo(
    () => (wf?.documents || []).map((d) => ({ ...d, docType: resolvedDocType(d) || null })),
    [wf],
  );
  const filtered = useMemo(() => {
    return docs.filter((d) => {
      // A specific document wins even when the index has no stored docType.
      if (focusDocId) return d.docId === focusDocId;
      if (docType) return (d.docType || '') === docType;
      return true;
    });
  }, [docs, focusDocId, docType]);
  const shown = focusDocId || docType ? filtered : docs;
  const byFolder = useMemo(() => {
    const m = {};
    docs.forEach((d) => { m[d.wellName || d.folder || 'Other'] = (m[d.wellName || d.folder || 'Other'] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [docs]);
  return (
    <div className="aw-dataroom" data-testid="dataroom">
      <div className="aw-grid-2">
        <div className="aw-card">
          <div className="aw-eyebrow">Ingestion log · adapters</div>
          <div className="aw-table-scroll"><table className="aw-table aw-table-dense">
            <thead><tr><th>Adapter</th><th>Source</th><th>Target</th><th>Rows</th><th>SHA-256</th></tr></thead>
            <tbody>
              {(wf?.provenance || []).map((p) => (
                <tr key={`${p.adapter}-${p.source}`}>
                  <td><span className="aw-pill aw-pill-green">ingested</span> {p.adapter}</td>
                  <td>{p.source}</td><td className="aw-small">{p.target}</td>
                  <td className="aw-mono aw-small">{Object.entries(p.rows || {}).map(([k, v]) => `${k}:${v}`).join(' ')}</td>
                  <td className="aw-mono aw-small" title={p.sha256}>{(p.sha256 || '').slice(0, 8)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <div className="aw-small aw-muted">The seller database is read in place on the private host; only derived JSON is published. Generated {wf?.generatedAt ? new Date(wf.generatedAt).toLocaleString() : '—'}.</div>
        </div>
        <div className="aw-card">
          <div className="aw-eyebrow">Inventory by well folder</div>
          {byFolder.length ? (
            byFolder.map(([k, v]) => (
              <div key={k} className="aw-ready-row"><span>{k}</span><div className="aw-bar"><i style={{ width: `${Math.min(100, (v / byFolder[0][1]) * 100)}%` }} /></div><span className="aw-mono aw-small">{v}</span></div>
            ))
          ) : (
            <div className="aw-empty" data-testid="dataroom-empty">No document index ingested.</div>
          )}
          <div className="aw-eyebrow" style={{ marginTop: 12 }}>Data issues ({(wf?.issues || []).length})</div>
          <ul className="aw-audit">
            {(wf?.issues || []).slice(0, 12).map((i, k) => (
              <li key={k}><span className="aw-chip aw-chip-amber">{i.kind}</span> {i.propnum || i.assetId || i.source || ''} {i.field ? `${i.field}: ${i.kept} ≠ ${i.other}` : ''}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className="aw-card">
        <div className="aw-toolbar">
          <div className="aw-eyebrow">Document index ({shown.length}{shown.length !== docs.length ? ` of ${docs.length}` : ''})</div>
          {(focusDocId || docType) && onClearFilter ? (
            <button type="button" className="aw-btn" onClick={onClearFilter}>Show all</button>
          ) : null}
        </div>
        {docs.length && !shown.length ? <div className="aw-empty">No indexed document matches this evidence link.</div> : null}
        <table className="aw-table aw-table-dense">
          <thead><tr><th>Document</th><th>Folder</th><th>Type</th><th>Tags</th><th>Status</th></tr></thead>
          <tbody>
            {shown.slice(0, 200).map((d) => (
              <tr key={d.docId} data-docid={d.docId} data-testid="doc-row" className={focusDocId && d.docId === focusDocId ? 'active' : ''}>
                <td>{d.title}</td>
                <td className="aw-small">{d.folder || d.wellName || ''}</td>
                <td className="aw-small">{typeLabel(d)}</td>
                <td className="aw-small">{(d.tags || []).join(', ')}</td>
                <td><span className="aw-pill aw-pill-blue">indexed</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
